import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ApiError } from '../api/client';
import { Alert } from './Field';
import { formatClock } from './VideoStage';

/** One assessment as the player manifest lists it: rules and standing only. */
export interface ManifestAssessment {
  id: string;
  title: string;
  description: string | null;
  required: boolean;
  questionCount: number;
  passingPercent: number;
  timeLimitMinutes: number | null;
  attemptsUsed: number;
  /** Null means unlimited. */
  attemptsAllowed: number | null;
  attemptsRemaining: number | null;
  inProgressAttemptId: string | null;
  recorded: { percent: number | null; outcome: 'PASS' | 'FAIL' | null } | null;
  passed: boolean;
  available: boolean;
  availability: 'OPEN' | 'IN_PROGRESS' | 'NOT_ENROLLED' | 'ATTEMPTS_EXHAUSTED' | 'COOLDOWN';
  nextAttemptAt: string | null;
}

interface Intro {
  id: string;
  title: string;
  description: string | null;
  questionCount: number;
  timeLimitMinutes: number | null;
  passingPercent: number;
  maxAttempts: number;
  cooldownMinutes: number;
  eligibility: {
    allowed: boolean;
    reason?: 'ATTEMPTS_EXHAUSTED' | 'COOLDOWN' | 'ALREADY_IN_PROGRESS';
    message: string;
    attemptsUsed: number;
    attemptsRemaining: number;
    nextAttemptAt?: string;
  };
  recorded: { percent: number | null; outcome: 'PASS' | 'FAIL' | null } | null;
  history: Array<{
    attemptNo: number;
    percent: number | null;
    outcome: 'PASS' | 'FAIL' | null;
    submittedAt: string;
  }>;
}

type QuestionType = 'MCQ_SINGLE' | 'MCQ_MULTI' | 'TRUE_FALSE' | 'SCENARIO';

interface PaperQuestion {
  id: string;
  type: QuestionType;
  /** Sanitised rich text from the API, like a lesson body. */
  stem: string;
  scenario: string | null;
  marks: number;
  options: Array<{ id: string; text: string }>;
  selectedOptionIds: string[];
}

interface Paper {
  attemptId: string;
  attemptNo: number;
  status: string;
  expiresAt: string | null;
  serverTime: string;
  timeLimitMinutes: number | null;
  passingPercent: number;
  questions: PaperQuestion[];
}

interface Result {
  attemptId: string;
  attemptNo: number;
  status: 'IN_PROGRESS' | 'SUBMITTED' | 'EXPIRED' | string;
  score: number | null;
  maxScore: number | null;
  percent: number | null;
  outcome: 'PASS' | 'FAIL' | null;
  passingPercent: number;
  attemptsRemaining: number;
  answersRevealed: boolean;
  certificate: { certificateNo: string; status: string; issuedNow: boolean } | null;
  review: Array<{
    questionId: string;
    stem: string;
    marks: number;
    marksAwarded: number | null;
    selectedOptionIds: string[] | null;
    countedIn: boolean;
    // Present only when the release policy has allowed the answer key.
    isCorrect?: boolean | null;
    explanation?: string | null;
    options?: Array<{ uuid: string; text: string; isCorrect?: boolean }>;
  }>;
}

/**
 * Which questions take several answers.
 *
 * MCQ_MULTI is the only type the API authors as "select all that apply".
 * SCENARIO is a stem with context; the scorer accepts any exact set, but the
 * paper deliberately carries no hint of how many options are right, so it is
 * shown as single-choice — which is how every scenario question is written
 * today. A multi-answer scenario would need the type changed, not a guess here.
 */
const isMultiple = (type: QuestionType) => type === 'MCQ_MULTI';

/** When to tell a screen reader how long is left. Never every second. */
const ANNOUNCE_AT_SECONDS = [600, 300, 60, 30];

/**
 * An assessment in the player's main pane: the rules, the paper, the result.
 *
 * Keyed by assessment id by its parent, so switching to another assessment
 * starts clean rather than showing one paper's state under another's title.
 * The server owns the clock and the score; this only shows them.
 */
export function QuizStage({
  assessment,
  lessonsDone,
  otherRequiredPending,
  onChanged,
}: {
  assessment: ManifestAssessment;
  /** Whether every lesson the course requires is complete. */
  lessonsDone: boolean;
  /** Other required assessments in the course not yet passed. */
  otherRequiredPending: number;
  /** Ask the player to reload its manifest (standing, progress). */
  onChanged: () => void;
}) {
  const [phase, setPhase] = useState<'intro' | 'paper' | 'result'>('intro');
  const [intro, setIntro] = useState<Intro | null>(null);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadIntro = useCallback(async () => {
    setError(null);
    try {
      setIntro(await api<Intro>(`/assessments/${assessment.id}`));
    } catch (caught) {
      setError((caught as ApiError).message);
    }
  }, [assessment.id]);

  useEffect(() => {
    void loadIntro();
  }, [loadIntro]);

  // Start and resume are the same call: the API hands back the running
  // attempt rather than opening a second clock.
  async function start() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api<Paper>(`/assessments/${assessment.id}/attempts`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setPaper(next);
      setPhase('paper');
      onChanged();
    } catch (caught) {
      setError((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'paper' && paper) {
    return (
      <QuizPaper
        title={assessment.title}
        paper={paper}
        onSubmitted={(scored) => {
          setResult(scored);
          setPhase('result');
          // The rail tick, the attempts left and the course's complete state
          // all move on a submission, and only the manifest knows them.
          onChanged();
          void loadIntro();
        }}
      />
    );
  }

  if (phase === 'result' && result) {
    return (
      <QuizResult
        assessment={assessment}
        result={result}
        lessonsDone={lessonsDone}
        otherRequiredPending={otherRequiredPending}
        busy={busy}
        onRetry={() => void start()}
        onBack={() => setPhase('intro')}
      />
    );
  }

  return (
    <QuizIntro
      assessment={assessment}
      intro={intro}
      error={error}
      busy={busy}
      onStart={() => void start()}
    />
  );
}

// ------------------------------------------------------------------ intro
function QuizIntro({
  assessment,
  intro,
  error,
  busy,
  onStart,
}: {
  assessment: ManifestAssessment;
  intro: Intro | null;
  error: string | null;
  busy: boolean;
  onStart: () => void;
}) {
  // The manifest summary renders at once; the intro call refines it with the
  // history and the server's own word on whether another attempt is allowed.
  const timeLimit = intro?.timeLimitMinutes ?? assessment.timeLimitMinutes;
  const questionCount = intro?.questionCount ?? assessment.questionCount;
  const passing = intro?.passingPercent ?? assessment.passingPercent;
  const unlimited = assessment.attemptsAllowed === null;
  const remaining = intro
    ? unlimited
      ? null
      : Math.max(0, intro.eligibility.attemptsRemaining)
    : assessment.attemptsRemaining;
  const resuming = intro
    ? intro.eligibility.reason === 'ALREADY_IN_PROGRESS'
    : assessment.availability === 'IN_PROGRESS';
  const allowed = intro ? intro.eligibility.allowed || resuming : assessment.available;
  const recorded = intro?.recorded ?? assessment.recorded;

  return (
    <div className="quiz">
      <div className="stage__head">
        <div>
          <p className="stage__eyebrow">
            Assessment · {assessment.required ? 'Required to finish the course' : 'Optional'}
          </p>
          <h2 className="stage__title">{assessment.title}</h2>
        </div>
        {assessment.passed ? (
          <span className="pill pill--active">✓ Passed</span>
        ) : assessment.required ? (
          <span className="pill pill--pending">Required</span>
        ) : null}
      </div>

      {assessment.description ? <p className="quiz__lead">{assessment.description}</p> : null}

      <dl className="quiz__facts">
        <div className="quiz__fact">
          <dt>Questions</dt>
          <dd>{questionCount}</dd>
        </div>
        <div className="quiz__fact">
          <dt>Pass mark</dt>
          <dd>{passing}%</dd>
        </div>
        <div className="quiz__fact">
          <dt>Time limit</dt>
          <dd>{timeLimit ? `${timeLimit} min` : 'None'}</dd>
        </div>
        <div className="quiz__fact">
          <dt>Attempts left</dt>
          <dd>
            {remaining === null
              ? 'Unlimited'
              : `${remaining} of ${assessment.attemptsAllowed ?? remaining}`}
          </dd>
        </div>
      </dl>

      {recorded && recorded.percent !== null ? (
        <p className="quiz__recorded">
          Your recorded result: <strong>{recorded.percent}%</strong>{' '}
          <span className={`pill pill--${recorded.outcome === 'PASS' ? 'active' : 'error'}`}>
            {recorded.outcome === 'PASS' ? 'Pass' : 'Not passed'}
          </span>
        </p>
      ) : null}

      {error ? <Alert kind="error" title={error} /> : null}

      {intro && !allowed ? (
        <Alert
          kind={intro.eligibility.reason === 'COOLDOWN' ? 'info' : 'warning'}
          title={
            intro.eligibility.reason === 'COOLDOWN' && intro.eligibility.nextAttemptAt
              ? `You can try again from ${new Date(intro.eligibility.nextAttemptAt).toLocaleString()}.`
              : intro.eligibility.message
          }
        />
      ) : null}

      {timeLimit && allowed && !resuming ? (
        <p className="muted quiz__note">
          The {timeLimit}-minute clock starts when you press Start and keeps running if you leave
          the page. Answers are saved as you go.
        </p>
      ) : null}
      {resuming ? (
        <p className="muted quiz__note">
          You have an attempt in progress. Your saved answers are still there.
        </p>
      ) : null}

      <div className="row quiz__actions">
        <button className="btn btn--sm" onClick={onStart} disabled={busy || !allowed}>
          {busy
            ? 'Opening…'
            : resuming
              ? 'Resume attempt'
              : (intro?.history.length ?? assessment.attemptsUsed) > 0
                ? 'Start a new attempt'
                : 'Start'}
        </button>
      </div>

      {intro && intro.history.length > 0 ? (
        <section className="quiz__history">
          <h3 className="quiz__subtitle">Your attempts</h3>
          <ul>
            {intro.history.map((attempt) => (
              <li key={attempt.attemptNo}>
                <span>Attempt {attempt.attemptNo}</span>
                <span>{attempt.percent ?? 0}%</span>
                <span className={`pill pill--${attempt.outcome === 'PASS' ? 'active' : 'error'}`}>
                  {attempt.outcome === 'PASS' ? 'Pass' : 'Not passed'}
                </span>
                <span className="muted">{new Date(attempt.submittedAt).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ paper
/**
 * The paper, one question at a time.
 *
 * One at a time rather than one long page because the API saves an answer per
 * question (POST /attempts/:id/answers) and stamps each with its arrival
 * time, and because a single focused question is what fits a 412px screen.
 * The numbered strip lets a learner jump about and see what is unanswered.
 */
function QuizPaper({
  title,
  paper,
  onSubmitted,
}: {
  title: string;
  paper: Paper;
  onSubmitted: (result: Result) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(paper.questions.map((question) => [question.id, question.selectedOptionIds])),
  );
  const [position, setPosition] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  // Refs, because the auto-submit fires from a timer and must see the latest
  // answers rather than the ones captured when the timer was set.
  const latest = useRef(answers);
  const unsaved = useRef(new Set<string>());
  const chains = useRef(new Map<string, Promise<unknown>>());
  const submittingRef = useRef(false);
  const heading = useRef<HTMLDivElement>(null);

  const questions = paper.questions;
  const question = questions[position];
  const unanswered = questions.filter((item) => !(answers[item.id]?.length)).length;

  // Focus follows the question, so a keyboard or screen-reader user lands on
  // the new stem instead of on a Next button that has moved.
  useEffect(() => {
    heading.current?.focus();
  }, [position]);

  /**
   * Save as the learner goes.
   *
   * Saves for one question are chained, so a quick change of mind cannot
   * arrive before the answer it replaced. Anything that fails stays marked
   * unsaved and is sent again with the submission. Only those are resent:
   * the API re-stamps every answer it receives, and resending one saved in
   * time would turn it into a late one on a timed paper.
   */
  function choose(target: PaperQuestion, optionId: string, checked: boolean) {
    if (submittingRef.current) return;
    const current = latest.current[target.id] ?? [];
    const next = isMultiple(target.type)
      ? checked
        ? [...current.filter((id) => id !== optionId), optionId]
        : current.filter((id) => id !== optionId)
      : [optionId];

    latest.current = { ...latest.current, [target.id]: next };
    setAnswers(latest.current);
    unsaved.current.add(target.id);

    const previous = chains.current.get(target.id) ?? Promise.resolve();
    const save = previous
      .catch(() => undefined)
      .then(() =>
        api(`/attempts/${paper.attemptId}/answers`, {
          method: 'POST',
          body: JSON.stringify({ questionId: target.id, selectedOptionIds: next }),
        }),
      )
      .then(() => {
        if (latest.current[target.id] === next) unsaved.current.delete(target.id);
        // The warning goes once a later save gets through for everything.
        if (unsaved.current.size === 0) setSaveFailed(false);
      })
      .catch(() => setSaveFailed(true));
    chains.current.set(target.id, save);
  }

  const submit = useCallback(
    async (force: boolean) => {
      if (submittingRef.current) return;
      const missing = questions.filter((item) => !(latest.current[item.id]?.length)).length;
      if (!force && missing > 0) {
        setConfirming(true);
        return;
      }
      submittingRef.current = true;
      setSubmitting(true);
      setConfirming(false);
      setError(null);

      await Promise.allSettled([...chains.current.values()]);
      const resend = [...unsaved.current].map((questionId) => ({
        questionId,
        selectedOptionIds: latest.current[questionId] ?? [],
      }));

      try {
        const scored = await api<Result>(`/attempts/${paper.attemptId}/submit`, {
          method: 'POST',
          body: JSON.stringify(resend.length > 0 ? { answers: resend } : {}),
        });
        onSubmitted(scored);
      } catch (caught) {
        submittingRef.current = false;
        setSubmitting(false);
        setError((caught as ApiError).message);
      }
    },
    [questions, paper.attemptId, onSubmitted],
  );

  // ------------------------------------------------------------- timer
  // Counted against the server's clock: the offset corrects a device whose
  // own clock is wrong, which would otherwise end the paper early or late.
  const offset = useMemo(
    () => new Date(paper.serverTime).getTime() - Date.now(),
    [paper.serverTime],
  );
  const deadline = paper.expiresAt ? new Date(paper.expiresAt).getTime() : null;
  const secondsLeft = useCallback(
    () => (deadline === null ? null : Math.max(0, Math.ceil((deadline - (Date.now() + offset)) / 1000))),
    [deadline, offset],
  );
  const [remaining, setRemaining] = useState<number | null>(secondsLeft);
  const announced = useRef(new Set<number>());

  useEffect(() => {
    if (deadline === null) return;
    const tick = () => setRemaining(secondsLeft());
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [deadline, secondsLeft]);

  useEffect(() => {
    if (remaining === null) return;
    // A few milestones, each said once. Saying the time every second would
    // drown out the question being read.
    for (const mark of ANNOUNCE_AT_SECONDS) {
      if (remaining <= mark && remaining > 0 && !announced.current.has(mark)) {
        // Marks already behind us when the paper opened (a resumed attempt)
        // are skipped, so only the nearest one is spoken.
        ANNOUNCE_AT_SECONDS.filter((other) => other >= mark).forEach((other) =>
          announced.current.add(other),
        );
        setAnnouncement(
          mark >= 60 ? `${Math.round(mark / 60)} minutes left.` : `${mark} seconds left.`,
        );
        break;
      }
    }
    // The API scores whatever reached it by the deadline (plus a short
    // grace), so submitting at zero is what makes those answers count.
    if (remaining === 0 && !submittingRef.current) {
      setAnnouncement('Time is up. Submitting your answers.');
      void submit(true);
    }
  }, [remaining, submit]);

  if (!question) {
    return <Alert kind="error" title="This paper has no questions." />;
  }

  const multiple = isMultiple(question.type);
  const chosen = answers[question.id] ?? [];
  const stemId = `quiz-stem-${question.id}`;
  const hintId = `quiz-hint-${question.id}`;
  const low = remaining !== null && remaining <= 60;

  return (
    <div className="quiz">
      <div className="quiz__bar">
        <div>
          <p className="stage__eyebrow">{title} · Attempt {paper.attemptNo}</p>
          <p className="quiz__progress">
            Question {position + 1} of {questions.length}
            <span className="muted"> · {questions.length - unanswered} answered</span>
          </p>
        </div>
        {remaining !== null ? (
          // role=timer is not live by default: readable on demand, silent
          // otherwise. The milestones go through the status region below.
          <span
            role="timer"
            aria-label="Time left"
            className={`quiz__timer${low ? ' quiz__timer--low' : ''}`}
          >
            ⏱ {formatClock(remaining)}
          </span>
        ) : null}
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      <nav className="quiz__nav" aria-label="Questions">
        {questions.map((item, index) => {
          const done = Boolean(answers[item.id]?.length);
          return (
            <button
              key={item.id}
              type="button"
              className={`quiz__dot${index === position ? ' quiz__dot--on' : ''}${
                done ? ' quiz__dot--done' : ''
              }`}
              aria-current={index === position ? 'step' : undefined}
              aria-label={`Question ${index + 1}, ${done ? 'answered' : 'not answered'}`}
              onClick={() => setPosition(index)}
            >
              {index + 1}
            </button>
          );
        })}
      </nav>

      <div className="quiz__q">
        <div
          id={stemId}
          ref={heading}
          tabIndex={-1}
          className="quiz__stem"
          // Sanitised by the API on save, as lesson bodies are.
          dangerouslySetInnerHTML={{ __html: question.stem }}
        />
        {question.scenario ? (
          <div className="quiz__scenario prose" dangerouslySetInnerHTML={{ __html: question.scenario }} />
        ) : null}

        <fieldset className="quiz__options" aria-labelledby={stemId} aria-describedby={hintId}>
          <p id={hintId} className="quiz__hint">
            {multiple ? 'Select all that apply.' : 'Select one answer.'}
            {question.marks !== 1 ? ` · ${question.marks} marks` : ''}
          </p>
          {question.options.map((option) => {
            const checked = chosen.includes(option.id);
            return (
              <label
                key={option.id}
                className={`quiz__option${checked ? ' quiz__option--on' : ''}`}
              >
                <input
                  type={multiple ? 'checkbox' : 'radio'}
                  name={`quiz-${question.id}`}
                  value={option.id}
                  checked={checked}
                  disabled={submitting}
                  onChange={(event) => choose(question, option.id, event.target.checked)}
                />
                <span dangerouslySetInnerHTML={{ __html: option.text }} />
              </label>
            );
          })}
        </fieldset>
      </div>

      {saveFailed ? (
        <Alert
          kind="warning"
          title="An answer could not be saved just now. It will be sent again when you submit."
        />
      ) : null}
      {error ? <Alert kind="error" title={error} /> : null}

      <nav className="quiz__steps" aria-label="Question navigation">
        <button
          type="button"
          className="btn btn--outline btn--sm"
          disabled={position === 0}
          onClick={() => setPosition((at) => Math.max(0, at - 1))}
        >
          ← Previous
        </button>
        {position < questions.length - 1 ? (
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setPosition((at) => Math.min(questions.length - 1, at + 1))}
          >
            Next →
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--sm quiz__submit"
          disabled={submitting}
          onClick={() => void submit(false)}
        >
          {submitting ? 'Submitting…' : 'Submit answers'}
        </button>
      </nav>

      {confirming ? (
        <SubmitConfirm
          unanswered={unanswered}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void submit(true)}
        />
      ) : null}
    </div>
  );
}

/**
 * Asks before submitting with questions left blank.
 *
 * The same `.modal` markup as ConfirmDialog, which is worded for deletions;
 * the reasoning for not using `window.confirm` is the same. Focus starts on
 * "Keep answering", the safe choice, so Enter does not end the attempt.
 */
function SubmitConfirm({
  unanswered,
  onCancel,
  onConfirm,
}: {
  unanswered: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    back.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="modal__backdrop" onClick={onCancel}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-confirm-title"
        style={{ maxWidth: 460 }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__head">
          <span className="modal__title" id="quiz-confirm-title">
            Submit with {unanswered} unanswered?
          </span>
          <button className="modal__close" onClick={onCancel} aria-label="Keep answering">
            ×
          </button>
        </div>
        <div className="modal__body">
          <p style={{ margin: 0 }}>
            {unanswered === 1 ? 'One question has' : `${unanswered} questions have`} no answer.
            Unanswered questions score zero, and a submitted attempt cannot be reopened.
          </p>
          <div className="row" style={{ marginTop: 16 }}>
            <button ref={back} className="btn btn--ghost btn--sm" type="button" onClick={onCancel}>
              Keep answering
            </button>
            <button className="btn btn--sm" type="button" onClick={onConfirm}>
              Submit anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- result
function QuizResult({
  assessment,
  result,
  lessonsDone,
  otherRequiredPending,
  busy,
  onRetry,
  onBack,
}: {
  assessment: ManifestAssessment;
  result: Result;
  lessonsDone: boolean;
  otherRequiredPending: number;
  busy: boolean;
  onRetry: () => void;
  onBack: () => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);

  const passed = result.outcome === 'PASS';
  const scored = result.outcome !== null;
  const unlimited = assessment.attemptsAllowed === null;
  const attemptsLeft = unlimited ? null : result.attemptsRemaining;
  const canRetry = !passed && (unlimited || result.attemptsRemaining > 0);

  return (
    <div className="quiz">
      <div className="quiz__result">
        <div className="state__art" aria-hidden="true">
          {passed ? '✓' : scored ? '↺' : '⏱'}
        </div>
        <h2 className="stage__title" ref={heading} tabIndex={-1}>
          {passed ? 'You passed' : scored ? 'Not passed this time' : 'This attempt was not scored'}
        </h2>
        {scored ? (
          <p className="quiz__score">
            {result.percent ?? 0}%
            <span className="muted">
              {' '}
              · {result.score ?? 0} of {result.maxScore ?? 0} marks · pass mark {result.passingPercent}%
            </span>
          </p>
        ) : (
          <p className="muted">
            Its time ran out before it was submitted, so nothing was marked.
          </p>
        )}
        {!passed ? (
          <p className="muted">
            {attemptsLeft === null
              ? 'You can try again as often as you need.'
              : attemptsLeft > 0
                ? `${attemptsLeft} attempt${attemptsLeft === 1 ? '' : 's'} left.`
                : 'You have used every attempt for this assessment.'}
          </p>
        ) : null}
      </div>

      {result.certificate?.issuedNow ? (
        <div className="alert alert--success" role="status">
          <span>
            <strong>Certificate issued.</strong> You have completed the course.{' '}
            <Link to="/my/certificates">View your certificate</Link>
          </span>
        </div>
      ) : result.certificate ? (
        <div className="alert alert--info" role="status">
          <span>
            Your certificate for this course is in <Link to="/my/certificates">My certificates</Link>.
          </span>
        </div>
      ) : passed && assessment.required ? (
        <Alert
          kind="info"
          title={
            !lessonsDone
              ? 'Passed. Finish the remaining lessons and your certificate is issued automatically.'
              : otherRequiredPending > 0
                ? `Passed. ${otherRequiredPending} more required assessment${
                    otherRequiredPending === 1 ? '' : 's'
                  } to go before the course is complete.`
                : 'Passed. Your certificate is being prepared — check My certificates shortly.'
          }
        />
      ) : null}

      <div className="row quiz__actions">
        {canRetry ? (
          <button className="btn btn--sm" onClick={onRetry} disabled={busy}>
            {busy ? 'Opening…' : 'Try again'}
          </button>
        ) : null}
        <button className="btn btn--ghost btn--sm" onClick={onBack}>
          Back to the assessment
        </button>
      </div>

      {result.answersRevealed ? (
        <section className="quiz__review">
          <h3 className="quiz__subtitle">Review</h3>
          <ol>
            {result.review.map((item) => {
              const chosen = new Set(item.selectedOptionIds ?? []);
              return (
                <li key={item.questionId} className="quiz__ritem">
                  <div className="quiz__rhead">
                    <div className="quiz__stem" dangerouslySetInnerHTML={{ __html: item.stem }} />
                    <span className={`pill pill--${item.isCorrect ? 'active' : 'error'}`}>
                      {item.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                    </span>
                  </div>
                  <ul className="quiz__ropts">
                    {(item.options ?? []).map((option) => (
                      <li
                        key={option.uuid}
                        className={`quiz__ropt${option.isCorrect ? ' quiz__ropt--correct' : ''}${
                          chosen.has(option.uuid) ? ' quiz__ropt--chosen' : ''
                        }`}
                      >
                        <span dangerouslySetInnerHTML={{ __html: option.text }} />
                        <span className="quiz__rtags">
                          {chosen.has(option.uuid) ? <span className="pill pill--inactive">Your answer</span> : null}
                          {option.isCorrect ? <span className="pill pill--active">Correct answer</span> : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {!item.countedIn ? (
                    <p className="quiz__hint">This answer arrived after the time limit and was not counted.</p>
                  ) : null}
                  {item.explanation ? (
                    <div className="quiz__explain prose" dangerouslySetInnerHTML={{ __html: item.explanation }} />
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>
      ) : scored ? (
        <p className="muted quiz__note">
          The correct answers are not released for this attempt.
        </p>
      ) : null}
    </div>
  );
}
